import React from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuthState } from 'react-firebase-hooks/auth';

import { firebaseClient } from '@cvt/clients/firebaseClient';
import { PermissionContext } from '@cvt/contexts';

import { routes } from '@shared/routes';

import { authClient } from '../../client/authClient';
import { useMe } from '../../hooks/useMe';
import { cacheKeys } from '../../config';

import { ContextProps } from './types';


export const defaultContext: ContextProps = {
  firebaseUser: undefined,
  user: undefined,
  login: () => {},
  impersonate: () => {},
  isImpersonating: false,
  exitImpersonation: () => {},
  resetPassword: () => {},
  sendHomeownerHubLoginLink: () => {},
  ssoLogin: () => {},
  logout: () => {},
  isLoggedIn: false,
  loading: false,
  error: undefined,
};

export const AuthContext = React.createContext(defaultContext);

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {

  const { resetPermissions } = React.useContext(PermissionContext);

  const [firebaseUser, firebaseLoading, error] = useAuthState(firebaseClient.getAuth());

  const queryClient = useQueryClient();

  const meEnabled = !!firebaseUser && !firebaseUser.isAnonymous;
  const { user, isLoading: userLoading } = useMe({
    enabled: meEnabled,
  });

  // The profile (`/me`) is fetched only after Firebase resolves, so treat the
  // app as still loading until it lands. Otherwise consumers briefly see an
  // authenticated-but-role-less state and role-based redirects misfire (e.g.
  // a hard refresh on a hub page bouncing to the dashboard).
  const loading = firebaseLoading || (meEnabled && userLoading);

  const isLoggedIn = React.useMemo(() => !!firebaseUser, [firebaseUser]);

  const isPWA = React.useMemo(() => {
    return window.hasOwnProperty('matchMedia') && window?.matchMedia('(display-mode: standalone)').matches;
  }, []);

  const ssoLogin = React.useCallback(async (provider: string) => {
    try {
      let ssoProvider: any = new firebaseClient.auth.GoogleAuthProvider();
      if(provider === 'twitter') {
        ssoProvider = new firebaseClient.auth.TwitterAuthProvider();
      }
      if(provider === 'microsoft') {
        ssoProvider = new firebaseClient.auth.OAuthProvider('microsoft.com');
      }
      if(provider === 'facebook') {
        ssoProvider = new firebaseClient.auth.FacebookAuthProvider();
      }
      if(provider === 'google') {
        ssoProvider.addScope('email');
        ssoProvider.addScope('profile');
        ssoProvider.setCustomParameters({ prompt: 'select_account' });
      }
      try {
        if(isPWA) {
          firebaseClient.auth.signInWithRedirect(ssoProvider);
          return Promise.resolve({
            success: true,
          });
        } else {
          const response = await firebaseClient.auth.signInWithPopup(ssoProvider);
          if (response.user) {
            return Promise.resolve({
              success: true,
            });
          } else {
            return Promise.reject({
              success: false,
              message: 'Wrong Email',
            });
          }
        }
      } catch(error: any) {
        return Promise.reject({
          success: false,
          error: error.code,
          message: error.message,
        });
      }
    } catch(error: any) {
      return Promise.reject({
        success: false,
        error: error.code,
        message: error.message,
      });
    }
  }, [isPWA]);

  const login = React.useCallback(async (email: string, password: string) => {
    try {
      const response = await firebaseClient.auth.signInWithEmailAndPassword(email, password);
      return Promise.resolve({
        success: !!response,
      });
    } catch (error: any) {
      return Promise.reject({
        success: false,
        error: error.code,
        message: error.message,
      });
    }
  }, []);

  // Both link emails go through our backend, not Firebase's own sender, so they
  // arrive in the 4i template from the 4i domain. Firebase still mints the link
  // and still completes the sign-in.
  const resetPassword = React.useCallback(async (email: string, turnstileToken: string) => {
    try {
      await authClient.sendPasswordResetLink(email, turnstileToken);
      return Promise.resolve({
        success: true,
      });
    } catch (error: any) {
      return Promise.reject({
        success: false,
        error: error.code,
        message: error.message,
      });
    }
  }, []);

  const sendHomeownerHubLoginLink = React.useCallback(async (email: string, turnstileToken: string) => {
    try {
      await authClient.sendHubSignInLink(email, turnstileToken);
      // signInWithEmailLink needs the address back when the link is opened;
      // stored before we navigate away so returning on this device is silent.
      window.localStorage.setItem('hubEmailForSignIn', email);
      return Promise.resolve({ success: true });
    } catch (error: any) {
      return Promise.reject({ success: false, error: error.code, message: error.message });
    }
  }, []);

  const impersonate = React.useCallback(async (token: string) => {
    try {
      queryClient.clear();
      const response = await firebaseClient.auth.signInWithCustomToken(token);
      return Promise.resolve({
        success: !!response,
      });
    } catch (error: any) {
      return Promise.reject({
        success: false,
        error: error.code,
        message: error.message,
      });
    }
  }, [queryClient]);

  // Fixed for the tab's lifetime: the flag is set right before a full reload.
  const isImpersonating = React.useMemo(() => firebaseClient.impersonation.isActive(), []);

  const exitImpersonation = React.useCallback(async () => {
    await firebaseClient.impersonation.stop();
    queryClient.clear();
    // The tab was opened from the leads table, so closing it hands the admin
    // back. Browsers ignore close() on a tab a script did not open (e.g. a
    // pasted URL); that one falls through to the leads page instead.
    window.close();
    window.location.replace(routes.leads.path);
  }, [queryClient]);

  const logout = React.useCallback(async () => {
    if (firebaseClient.impersonation.isActive()) {
      return exitImpersonation();
    }
    await firebaseClient.auth.signOut();
    resetPermissions();
    queryClient.removeQueries({
      queryKey: [cacheKeys.getMe],
    });
    queryClient.clear();
  }, [queryClient, resetPermissions, exitImpersonation]);

  return (
    <AuthContext.Provider
      value={{
        user,
        firebaseUser,
        logout,
        login,
        ssoLogin,
        resetPassword,
        sendHomeownerHubLoginLink,
        impersonate,
        isImpersonating,
        exitImpersonation,
        isLoggedIn,
        loading,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const AuthConsumer = AuthContext.Consumer;
